import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SectionHeader } from "./SectionHeader";
import { NumberInput } from "./NumberInput";
import { FactorControls } from "./FactorControls";
import type { KeywordSalesTier } from "./types";

interface KeywordSalesScoringCardProps {
  keywordSalesMax: number;
  onKeywordSalesMaxChange: (v: number) => void;
  keywordSalesTiers: KeywordSalesTier[];
  onTierPointsChange: (idx: number, v: number) => void;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
}

export function KeywordSalesScoringCard({
  keywordSalesMax,
  onKeywordSalesMaxChange,
  keywordSalesTiers,
  onTierPointsChange,
  enabled,
  onEnabledChange,
}: KeywordSalesScoringCardProps) {
  return (
    <Card id="kps-module-keyword-sales" className={cn(!enabled && "bg-muted/30 order-1")}>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <SectionHeader
            title="Keyword Sales Scoring"
            description="Monthly revenue the hero ASIN is driving on this keyword. The most direct signal of commercial value."
          />
          <FactorControls
            id="kps-toggle-keyword-sales"
            enabled={enabled}
            onEnabledChange={onEnabledChange}
            value={keywordSalesMax}
            onValueChange={onKeywordSalesMaxChange}
          />
        </div>

        <div className={cn("space-y-4", !enabled && "opacity-50 pointer-events-none select-none")}>
          <div className="space-y-0">
            <div className="grid grid-cols-[1fr_120px] gap-4 items-center px-1 h-9 border-b border-border">
              <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Monthly Keyword Sales</span>
              <span className="text-xs font-semibold text-foreground uppercase tracking-wide text-right">Points</span>
            </div>
            {keywordSalesTiers.map((row, idx) => (
              <div
                key={row.label}
                className={cn(
                  "grid grid-cols-[1fr_120px] gap-4 items-center px-3 py-3 border-b border-border last:border-b-0 -mx-3",
                  row.highlighted && (row.sweetSpot ? "bg-status-success-soft" : "bg-status-success-soft/50")
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-foreground">{row.label}</span>
                  {row.sweetSpot && (
                    <Badge className="bg-[hsl(155_45%_75%)] text-foreground hover:bg-[hsl(155_45%_75%)]">
                      Sweet spot
                    </Badge>
                  )}
                </div>
                <div className="flex justify-end">
                  <NumberInput
                    value={row.points}
                    min={0}
                    max={keywordSalesMax}
                    onValueChange={(v) => onTierPointsChange(idx, v)}
                    className="w-20 h-8 text-sm text-center rounded-md bg-card tabular-nums"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
